import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionExpiryComponent } from './dialog/session-expiry/session-expiry.component';
import { SharedModule } from '../shared/shared.module';
import {
    ActionSheetModule, BarModule,
    BusyIndicatorModule,
    ButtonModule,
    CheckboxModule,
    FormControlModule,
    FormInputMessageGroupModule,
    FormLabelModule,
    FormMessageModule, FormModule,
    IconModule,
    InfiniteScrollModule,
    LayoutGridModule,
    LayoutPanelModule,
    LinkModule,
    ListModule,
    MenuModule,
    MessageStripModule,
    MultiInputModule,
    NestedListModule,
    PanelModule,
    PopoverModule,
    SegmentedButtonModule,
    SelectModule,
    SideNavigationModule,
    StepInputModule,
    TableModule,
    TextModule,
    TitleModule,
    ToolbarModule,
    ShellbarModule,
} from '@fundamental-ngx/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HomeComponent } from './components/core/home/home.component';
import { HeaderComponent } from './components/core/header/header.component';
import { FclComponent } from './components/abstract/fcl/fcl.component';
import { ResourceFclComponent } from './components/abstract/resource-fcl/resource-fcl.component';
import { AbstractContentComponent } from './components/abstract/abstract-content/abstract-content.component';
import { ResourcePageComponent } from './components/abstract/resource-page/resource-page.component';
import { SideNavigationComponent } from './components/core/side-navigation/side-navigation.component';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UserInfoDialogComponent } from './dialog/user-info/user-info.component';
import { SupportDialogComponent } from './dialog/support-dialog/support-dialog.component';
import { BreadcrumbsComponent } from './components/core/breadcrumbs/breadcrumbs.component';
import { FormlyModule } from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import {
    constValidationMessage,
    exclusiveMaximumValidationMessage,
    exclusiveMinimumValidationMessage,
    maxItemsValidationMessage,
    maxlengthValidationMessage,
    maxValidationMessage,
    minItemsValidationMessage,
    minlengthValidationMessage,
    minValidationMessage,
    multipleOfValidationMessage,
    patternValidationMessage,
} from './helpers/schema/SchemaUtils';
import { CheckboxComponent } from './helpers/schema/controls/checkbox/checkbox.component';
import { InputComponent } from './helpers/schema/controls/input/input.component';
import { SelectComponent } from './helpers/schema/controls/select/select.component';
import { ArrayComponent } from './helpers/schema/types/array/array.component';
import { FormFieldWrapperComponent } from './helpers/schema/types/form-field-wrapper/form-field-wrapper.component';
import { NullComponent } from './helpers/schema/types/null/null.component';
import { ObjectComponent } from './helpers/schema/types/object/object.component';
import { YamlComponent } from './helpers/schema/controls/yaml/yaml.component';
import { MultischemaComponent } from './helpers/schema/types/multischema/multischema.component';
import {PlatformMenuModule} from "@fundamental-ngx/platform";
import {ScrollingModule} from "@angular/cdk/scrolling";
import {CsrfInterceptor} from "./interceptors/csrf/csrf.interceptor";
import {HTTP_INTERCEPTORS} from "@angular/common/http";
import {GoToPathComponent} from "./components/core/header/go-to-path/go-to-path.component";
import {RecentListComponent} from "./components/core/header/go-to-path/recent/recent-list.component";
import {CADIconComponent} from "./components/core/side-navigation/cad-icon/icon.component";

@NgModule({
    declarations: [
        GoToPathComponent,
        RecentListComponent,
        SessionExpiryComponent,
        SideNavigationComponent,
        HomeComponent,
        HeaderComponent,
        FclComponent,
        ResourceFclComponent,
        AbstractContentComponent,
        ResourcePageComponent,
        UserInfoDialogComponent,
        SupportDialogComponent,
        BreadcrumbsComponent,
        CheckboxComponent,
        InputComponent,
        SelectComponent,
        ArrayComponent,
        FormFieldWrapperComponent,
        NullComponent,
        ObjectComponent,
        YamlComponent,
        MultischemaComponent,
        GoToPathComponent,
        CADIconComponent
    ],
    imports: [
        SharedModule,
        CommonModule,
        BrowserAnimationsModule,
        ListModule,
        ButtonModule,
        FormsModule,
        TableModule,
        FormLabelModule,
        LayoutPanelModule,
        NestedListModule,
        InfiniteScrollModule,
        PopoverModule,
        SideNavigationModule,
        RouterModule,
        ToolbarModule,
        IconModule,
        LinkModule,
        TitleModule,
        SegmentedButtonModule,
        MenuModule,
        ActionSheetModule,
        BusyIndicatorModule,
        TextModule,
        ShellbarModule,
        FormlyModule.forRoot({
            extras: {resetFieldOnHide: true},
            validationMessages: [
                {name: 'required', message: 'This field is required'},
                {name: 'null', message: 'Must be null'},
                {name: 'minlength', message: minlengthValidationMessage},
                {name: 'maxlength', message: maxlengthValidationMessage},
                {name: 'min', message: minValidationMessage},
                {name: 'max', message: maxValidationMessage},
                {name: 'multipleOf', message: multipleOfValidationMessage},
                {name: 'exclusiveMinimum', message: exclusiveMinimumValidationMessage},
                {name: 'exclusiveMaximum', message: exclusiveMaximumValidationMessage},
                {name: 'minItems', message: minItemsValidationMessage},
                {name: 'maxItems', message: maxItemsValidationMessage},
                {name: 'uniqueItems', message: 'Must NOT have duplicate items'},
                {name: 'const', message: constValidationMessage},
                {name: 'pattern', message: patternValidationMessage},
            ],
            wrappers: [{name: 'form-wrapper', component: FormFieldWrapperComponent}],
            types: [
                {name: 'input', component: InputComponent, wrappers: ['form-wrapper']},
                {name: 'checkbox', component: CheckboxComponent},
                {name: 'select', component: SelectComponent},
                {name: 'yaml', component: YamlComponent},

                {name: 'string', extends: 'input'},
                {
                    name: 'number',
                    extends: 'input',
                    defaultOptions: {
                        templateOptions: {
                            type: 'number',
                        },
                    },
                },
                {
                    name: 'integer',
                    extends: 'input',
                    defaultOptions: {
                        templateOptions: {
                            type: 'number',
                        },
                    },
                },
                {name: 'boolean', extends: 'checkbox'},
                {name: 'enum', extends: 'select', wrappers: ['form-wrapper']},
                {name: 'null', component: NullComponent, wrappers: ['form-wrapper']},
                {name: 'array', component: ArrayComponent, wrappers: ['form-wrapper']},
                {name: 'object', component: ObjectComponent},
                {name: 'multischema', component: MultischemaComponent},
            ],
        }),
        FormlySelectModule,
        FormMessageModule,
        FormInputMessageGroupModule,
        FormControlModule,
        ReactiveFormsModule,
        MultiInputModule,
        SelectModule,
        MessageStripModule,
        CheckboxModule,
        StepInputModule,
        LayoutGridModule,
        PanelModule,
        PlatformMenuModule,
        ScrollingModule,
        FormModule,
        BarModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CsrfInterceptor,
            multi: true
        }
    ],
    exports: [
        SessionExpiryComponent,
        SideNavigationComponent,
        HomeComponent,
        HeaderComponent,
        FclComponent,
        ResourceFclComponent,
        AbstractContentComponent,
        ResourcePageComponent,
        BreadcrumbsComponent,
        CADIconComponent,
        CheckboxComponent
    ],
})
export class CoreModule {}
